import {Group, GroupedList} from '@wandb/weave/common/util/data';
import * as _ from 'lodash';

import {ClassLabelNode} from '../../util/media';
import {Axis, NonEmptyTile} from './common';
import {calcGroupCount, Layout, MediaConfigWithDefaults} from './Component';
import {EmptyTile, Tile} from './tiles';
import {StepMeta} from './types';
import {getStepFromSliderStep} from './utils/stepUtils';

interface RunInfo {
  name: string;
  displayName: string;
  entityName: string;
  projectName: string;
  history: any[] | null;
  notes?: string;
  color: string | undefined;
  _wandb: any;
}

interface LayoutInput {
  runs: RunInfo[];
  stepMeta: StepMeta;
  config: MediaConfigWithDefaults;
  allMasks: {
    [mediaKey: string]: {
      [maskKey: string]: ClassLabelNode;
    };
  };
  currentStep: number;
}

// Axis calculation helpers
function calcStepAxis(
  stepMeta: StepMeta,
  opts: {maxSize?: number; min?: number; max?: number} = {}
): number[] {
  const min = opts.min ?? -Infinity;
  const max = opts.max ?? Infinity;
  const stepsInRange = (stepMeta.sliderSteps ?? stepMeta.steps).filter(
    s => s >= min && s <= max
  );
  const maxSize = opts.maxSize;
  if (maxSize && stepsInRange.length > maxSize) {
    const step = Math.floor(stepsInRange.length / maxSize);
    const stepData: number[] = [];
    for (let i = 0; i < maxSize; i++) {
      stepData.push(stepsInRange[i * step]);
    }
    return stepData;
  }
  return stepsInRange;
}

function calcRunAxis(runs: RunInfo[], opts: {maxSize?: number} = {}): number[] {
  const maxSize = opts.maxSize;
  return _.range(0, runs.slice(0, maxSize).length);
}

export function calcIndexAxis(
  stepMeta: StepMeta,
  opts: {maxSize?: number} = {}
): number[] {
  const maxSize = opts.maxSize;
  const indices = stepMeta.indices;
  return maxSize && indices.length > maxSize
    ? _.take(indices, maxSize)
    : indices;
}

function calcDefaultAxis(): number[] {
  return _.range(0, 20);
}

function addGroupOrTileForMediaKeys(
  mediaKeys: string[] | undefined,
  tiles: GroupedList<Tile>,
  newTile: Omit<NonEmptyTile, 'mediaKey'>
): Tile | Group<Tile> {
  if (mediaKeys == null || mediaKeys.length === 0) {
    throw new Error(`empty mediaKeys`);
  }
  // Create a single tile or a group of tiles depending
  // the number of media keys set

  if (mediaKeys.length === 1) {
    const tile = {
      mediaKey: mediaKeys[0],
      ...newTile,
    };
    tiles.push(tile);
    return tile;
  }

  for (const mediaKey of mediaKeys) {
    const tile = {
      mediaKey,
      ...newTile,
    };
    tiles.push({groupType: 'mediaKeys', items: [tile]});
  }
  return tiles[tiles.length - 1];
}

function createTile(
  run: RunInfo,
  runStepMeta: any,
  mediaStep: number,
  sliderStepIndex: number | undefined,
  tileMediaIndex: number,
  meta?: {camera: {cameraIndex: number}}
): Omit<NonEmptyTile, 'mediaKey'> {
  return {
    mediaSliderKey: runStepMeta.sliderKey,
    mediaSliderKeyValue: sliderStepIndex,
    mediaStep,
    mediaIndex: tileMediaIndex,
    run: {
      ...run,
      history: run.history ?? [],
      _wandb: run._wandb,
      color: run.color,
    },
    runSignature: {
      entityName: run.entityName,
      projectName: run.projectName,
      runName: run.name,
    },
    ...(meta && {meta}),
  };
}

function createGalleryTiles(
  input: LayoutInput,
  axisData: number[],
  tiles: GroupedList<Tile>,
  gallerySettings: {axis: Axis},
  maxGalleryItems: number | undefined,
  stepFromConfig: number | undefined,
  mediaIndex: number | undefined,
  snapToExistingStep: boolean
): number[] {
  const {runs, stepMeta, config} = input;
  const {mediaKeys} = config;

  // Limit the number of items we process based on maxGalleryItems
  const itemsToProcess = maxGalleryItems
    ? Math.min(axisData.length, maxGalleryItems)
    : axisData.length;

  for (let i = 0; i < itemsToProcess; i++) {
    const runIndex = gallerySettings.axis === 'run' ? i : 0;
    const run = runs[runIndex];
    const runStepMeta = stepMeta.runs[runIndex];
    const {step: stepIndex, sliderStep: sliderStepIndex} =
      getStepFromSliderStep(runStepMeta, input.currentStep ?? stepFromConfig);
    const mediaStep = gallerySettings.axis === 'step' ? axisData[i] : stepIndex;
    const tileMediaIndex =
      gallerySettings.axis === 'index'
        ? (axisData[i] as number)
        : (mediaIndex ?? 0);

    const meta =
      gallerySettings.axis === 'camera'
        ? {camera: {cameraIndex: axisData[i]}}
        : undefined;

    const tile = createTile(
      run,
      runStepMeta,
      mediaStep,
      sliderStepIndex,
      tileMediaIndex,
      meta
    );
    addGroupOrTileForMediaKeys(mediaKeys, tiles, tile);
  }

  return axisData.slice(0, itemsToProcess);
}

function fillEmptyTiles(
  tiles: GroupedList<Tile>,
  emptyTilesNeeded: number,
  mediaKeys: string[] | undefined
): void {
  if (emptyTilesNeeded > 0) {
    const emptyTiles = Array(emptyTilesNeeded).fill({
      mediaKey: mediaKeys?.[0] ?? '',
      isEmpty: true,
    } as EmptyTile);
    tiles.push(...emptyTiles);
  }
}

function createGridTiles(
  input: LayoutInput,
  xAxisData: number[],
  yAxisData: number[],
  tiles: GroupedList<Tile>,
  gridSettings: {xAxis: Axis; yAxis: Axis},
  columnCount: number,
  groupCount: number
): void {
  const {runs, stepMeta, config, allMasks} = input;
  const {
    mediaKeys,
    mediaIndex,
    stepIndex: stepFromConfig,
    snapToExistingStep,
  } = config;
  const {xAxis, yAxis} = gridSettings;

  for (let row = 0; row < yAxisData.length; row++) {
    const addedToRow: GroupedList<Tile> = [];
    let tilesAddedToRow = 0;

    for (let col = 0; col < columnCount / groupCount; col++) {
      if (col >= xAxisData.length) {
        fillEmptyTiles(tiles, columnCount - tilesAddedToRow, mediaKeys);
        break;
      }

      const runIndex = xAxis === 'run' ? col : yAxis === 'run' ? row : 0;
      const run = runs[runIndex];
      const runStepMeta = stepMeta.runs[runIndex];

      const stepIndex =
        xAxis === 'step'
          ? xAxisData[col]
          : yAxis === 'step'
            ? yAxisData[row]
            : (stepFromConfig ?? runStepMeta.steps[0] ?? 0);

      const {step: mediaStep, sliderStep: sliderStepIndex} =
        getStepFromSliderStep(runStepMeta, stepIndex, snapToExistingStep);

      const tileMediaIndex =
        xAxis === 'index'
          ? (xAxisData[col] as number)
          : yAxis === 'index'
            ? yAxisData[row]
            : (mediaIndex ?? 0);

      const tile = createTile(
        run,
        runStepMeta,
        mediaStep,
        sliderStepIndex,
        tileMediaIndex,
        xAxis === 'camera'
          ? {camera: {cameraIndex: xAxisData[col]}}
          : yAxis === 'camera'
            ? {camera: {cameraIndex: yAxisData[row]}}
            : undefined
      );

      const addedTile = addGroupOrTileForMediaKeys(mediaKeys, tiles, tile);
      addedToRow.push(addedTile);

      // Count actual tiles added (accounting for groups)
      if ('items' in addedTile) {
        tilesAddedToRow += addedTile.items.length;
      } else {
        tilesAddedToRow += 1;
      }
    }
  }
}

export function makeLayout(input: LayoutInput): Layout {
  const {
    mediaKeys,
    gallerySettings,
    gridSettings,
    mode,
    maxGalleryItems,
    stepIndex: stepFromConfig,
    mediaIndex,
    columnCount,
    selection,
    maxYAxisCount,
    snapToExistingStep = true,
  } = input.config;

  // Early return if we don't have the required data
  if (input.runs.length === 0 || input.stepMeta.runs.length === 0) {
    return {tiles: []};
  }

  let historySampleRate: number | undefined;

  const getAxisData = (
    axis: Axis,
    opts?: {maxSize?: number; min?: number; max?: number}
  ): number[] => {
    if (axis === 'step') {
      const data = calcStepAxis(input.stepMeta, opts ?? {});
      if (data.length > 1) {
        historySampleRate = (data[data.length - 1] - data[0] - 2) / data.length;
      }
      return data;
    }
    if (axis === 'run') {
      return calcRunAxis(input.runs, opts ?? {});
    }
    if (axis === 'index') {
      return calcIndexAxis(input.stepMeta, opts ?? {});
    }
    return calcDefaultAxis();
  };

  const tiles: GroupedList<Tile> = [];

  let galleryAxisData: number[] | null = null;
  let gridAxisData: {x: number[]; y: number[]} | null = null;
  let xAxisTicks: number[] | undefined = undefined;
  let allXAxisValues: number[] | undefined = undefined;

  if (mode === 'gallery') {
    const axisData = getAxisData(gallerySettings.axis, {
      maxSize: maxGalleryItems,
    });
    galleryAxisData = createGalleryTiles(
      input,
      axisData,
      tiles,
      gallerySettings,
      maxGalleryItems,
      stepFromConfig,
      mediaIndex,
      snapToExistingStep
    );
  } else if (mode === 'grid') {
    const {xAxis, yAxis} = gridSettings;
    xAxisTicks = getAxisData(xAxis, {
      maxSize: columnCount,
    });
    allXAxisValues = getAxisData(xAxis);

    const [minX, maxX] = [
      _.get(selection?.xAxis, 0),
      _.get(selection?.xAxis, 1),
    ];
    const [minY, maxY] = [
      _.get(selection?.yAxis, 0),
      _.get(selection?.yAxis, 1),
    ];
    const groupSize = mediaKeys?.length ?? 0;

    const xAxisData = getAxisData(xAxis, {
      maxSize: columnCount / groupSize,
      min: minX,
      max: maxX,
    });
    const yAxisData = getAxisData(yAxis, {
      maxSize: maxYAxisCount,
      min: minY,
      max: maxY,
    });
    gridAxisData = {x: xAxisData, y: yAxisData};

    const groupCount = calcGroupCount(input.config);
    createGridTiles(
      input,
      xAxisData,
      yAxisData,
      tiles,
      gridSettings,
      columnCount,
      groupCount
    );
  }

  return {
    tiles,
    gridAxisData,
    galleryAxisData,
    xAxisTicks,
    allXAxisValues,
    historySampleRate,
  };
}
